import { Box, Tooltip, ClickAwayListener, useTheme, useMediaQuery, Typography } from '@mui/material';
import HotIconButton from '../../popups/custom/ShareLinkPopup/HotIconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { generateTestId } from 'app/functions';

const ShareButton = ({
    type,
    icon = null,
    iconImage = null,
    onClick,
    isMobile,
    theme,
    label,
    color,
    tooltipContent,
    tooltipOpen,
    onTooltipClose,
    tooltipPlacement = 'bottom',
    tooltipProps = {},
    canShare = true,
    compact = false,
    useClickAwayListener = false,
    testID = 'share-button',
}) => {

    const getIconSize = (canShare, isMobile) => {
        if (canShare) {
            return isMobile ? '55px' : '60px';
        }
        return isMobile ? '45px' : '62px';
    };

    const getLabelSize = (canShare, isMobile) => {
        if (canShare) {
            return isMobile ? '14px' : '16px';
        }
        return isMobile ? '14px' : '18px';
    };

    const iconMap = {
        copy: ContentCopyIcon,
        share: ShareIcon,
        email: EmailIcon,
        sms: SmsIcon,
        whatsapp: WhatsAppIcon,
    };

    const iconSize = compact ? '100%' : getIconSize(canShare, isMobile);
    const labelSize = getLabelSize(canShare, isMobile);
    const primaryColor = color || (compact
        ? theme.palette.primary?.blue
        : theme.palette.primary?.main);
    

    const IconComponent = icon ||iconMap[type];
    if (!IconComponent) return null;

    const getButtonPadding = (canShare, isMobile) => {
        if (!canShare && isMobile) {
            return '12px 10px';
        }
        return isMobile ? '16px 12px' : '12px 10px';
    };

    const getIconButtonMaxWidth = (canShare, isMobile) => {
        if (!canShare && isMobile) {
            return '80px';
        }
        return isMobile ? '160px' : '100px';
    };

    const buttonPadding = getButtonPadding(canShare, isMobile);
    const iconButtonMaxWidth = getIconButtonMaxWidth(canShare, isMobile);   

    const buttonContent = (
        <Box 
            onClick={onClick}
            sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: compact ? 'center' : 'flex-start',
                width: '100%',
                height: '100%',
                flex: compact ? 'unset' : 1,
            }}
        >
            <HotIconButton
                flex={compact ? 'unset' : 1}
                aspectRatio={compact ? 'unset' : 1}
                maxWidth={compact ? '100%' : iconButtonMaxWidth}
                padding={0}
                testID={generateTestId(testID, 'icon-button')}
                sx={{ 
                    background: 'transparent',
                    width: compact ? '100%' : (!canShare && isMobile ? 'auto' : '100%'),
                    height: compact ? '100%' : 'auto',
                    minWidth: compact ? 0 : undefined,
                    minHeight: compact ? 0 : undefined,
                    '&:hover': {
                        background: 'transparent',
                    },
                }}
            >
                {iconImage ? (
                    <Box
                        component="img"
                        src={iconImage}
                        alt={label || type}
                        sx={{
                            width: iconSize,
                            height: iconSize,
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                            display: 'block',
                            transition: 'transform 0.3s ease',
                        }}
                    />
                ) : (
                    <IconComponent 
                        sx={{ 
                            fontSize: iconSize, 
                            color: primaryColor,
                            transition: 'transform 0.3s ease',
                        }} 
                    />
                )}
            </HotIconButton>
            {label && (
                    <Typography
                        component="div"
                        sx={{
                            fontSize: labelSize,
                            color: theme.palette.primary?.blue || '#000',
                            fontFamily: theme.typography.fontFamilyBold,
                            textAlign: 'center',
                            fontStyle: 'bold',
                            lineHeight: 1.4,
                            userSelect: 'none',
                            pointerEvents: 'none',
                            whiteSpace: 'nowrap',
                            width: '100%',
                            margin: 0,
                            padding: 0,
                            display: 'block',
                            transition: 'color 0.3s ease',
                        }}
                        testID={generateTestId(testID, 'label')}
                    >
                        {label}
                    </Typography>
            )}
        </Box>
    );

    const wrapper = (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, width: '100%' }} testID={generateTestId(testID, 'wrapper')}>
            {tooltipContent ? (
                <Tooltip
                    open={!!tooltipOpen}
                    onClose={onTooltipClose}
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                    placement={tooltipPlacement}
                    arrow
                    title={tooltipContent}
                    testID={generateTestId(testID, 'tooltip')}
                    componentsProps={tooltipProps.componentsProps}
                    slotProps={tooltipProps.slotProps}
                >
                    {buttonContent}
                </Tooltip>
            ) : (
                buttonContent
            )}
        </Box>
    );

    if (useClickAwayListener && tooltipContent) {
        return (
            <ClickAwayListener onClickAway={onTooltipClose}>
                {wrapper}
            </ClickAwayListener>
        );
    }

    return wrapper;
};

export default ShareButton;
